import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { tenderService } from '$services'
import {
  enqueueLoadedSnackbar,
  exceptionMessageHandler,
  roundNumber,
} from '$utils'
import { TenderLineUi, TenderUi } from './types'
import {
  convertTenderToTenderUi,
  convertTenderUiToTender,
} from './helpers/helper'
import useStockStore from '../../store'
import { IItem, Tender } from '$models'
import { createTenderLine } from './utils'

interface TendersState {
  tenders: Tender[]
  tenderUi?: TenderUi
}

export interface TendersSlice extends TendersState {
  resetTendersSlice: () => void
  getTenders: () => Promise<void>
  deleteTender: (id?: string) => Promise<void>
  getTender: (id: string) => Promise<void>
  setTenderUi: (tender: TenderUi) => void
  addTenderLine: (item: IItem) => void
  removeTenderLine: (lineId: string) => void
  updateTenderLine: (lineId: string, updates: Partial<TenderLineUi>) => void
  handleUnitChange: (
    lineId: string,
    newUnitId: string,
    newUnitName: string,
    newUnitRatio: number
  ) => void
  handleBrandChange: (
    lineId: string,
    newBrandId: string,
    newBrandName: string
  ) => void
  initNewTender: () => void
  saveTender: () => Promise<boolean>
  addItemGroup: (groupId: string) => void
}

const initialTendersState: TendersState = {
  tenders: [],
  tenderUi: undefined,
}

const createTendersSlice: StateCreator<
  TendersSlice & SharedSlice,
  [],
  [],
  TendersSlice
> = (set, get) => ({
  ...initialTendersState,

  resetTendersSlice: () => set(initialTendersState),

  getTenders: async () => {
    try {
      const tenders = await tenderService.getTenders()
      set({ tenders })
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to load tenders')
      set({ tenders: [] })
    }
  },

  deleteTender: async (id?: string) => {
    if (!id)
      return exceptionMessageHandler(
        new Error('Tender ID is required for deletion'),
        'Failed to delete tender'
      )

    const previousTenders = get().tenders
    try {
      set((state) => ({
        tenders: state.tenders.filter((t) => t.id !== id),
      }))
      await tenderService.deleteTender(id)
      enqueueLoadedSnackbar('Tender deleted', 'success')
    } catch (error) {
      set({ tenders: previousTenders })
      exceptionMessageHandler(error, 'Failed to delete tender')
    }
  },

  getTender: async (id: string) => {
    try {
      const tender = await tenderService.getTender(id)
      set({ tenderUi: convertTenderToTenderUi(tender) })
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to load tender')
    }
  },

  setTenderUi: (tender: TenderUi) => set({ tenderUi: tender }),

  addTenderLine: (item: IItem) => {
    const tenderUi = get().tenderUi
    if (!tenderUi) return

    const exists = tenderUi.lines.some((l) => l.stockItemId === item.id)
    if (exists) return

    set({
      tenderUi: {
        ...tenderUi,
        lines: [...tenderUi.lines, createTenderLine(item)],
      },
    })
  },

  removeTenderLine: (lineId: string) => {
    const tenderUi = get().tenderUi
    if (!tenderUi) return

    set({
      tenderUi: {
        ...tenderUi,
        lines: tenderUi.lines.filter((l) => l.lineId !== lineId),
      },
    })
  },

  updateTenderLine: (lineId: string, updates: Partial<TenderLineUi>) => {
    const tenderUi = get().tenderUi
    if (!tenderUi) return

    const normalizedUpdates: Partial<TenderLineUi> =
      typeof updates.unitCost === 'number'
        ? { ...updates, unitCost: roundNumber(updates.unitCost, 2) }
        : updates

    set({
      tenderUi: {
        ...tenderUi,
        lines: tenderUi.lines.map((l) =>
          l.lineId === lineId ? { ...l, ...normalizedUpdates } : l
        ),
      },
    })
  },

  handleUnitChange: (
    lineId: string,
    newUnitId: string,
    newUnitName: string,
    newUnitRatio: number
  ) => {
    const tenderUi = get().tenderUi
    if (!tenderUi) return

    const line = tenderUi.lines.find((l) => l.lineId === lineId)
    if (!line) return

    // unitCost is display-unit cost — recalculate when unit changes
    const oldRatio = line.unitRatio ?? 1
    const newUnitCost = line.unitCost
      ? roundNumber((line.unitCost / oldRatio) * newUnitRatio, 2)
      : 0

    set({
      tenderUi: {
        ...tenderUi,
        lines: tenderUi.lines.map((l) =>
          l.lineId === lineId
            ? {
                ...l,
                unitId: newUnitId,
                unitName: newUnitName,
                unitRatio: newUnitRatio,
                unitCost: newUnitCost,
              }
            : l
        ),
      },
    })
  },

  handleBrandChange: (
    lineId: string,
    newBrandId: string,
    newBrandName: string
  ) => {
    const tenderUi = get().tenderUi
    if (!tenderUi) return

    const line = tenderUi.lines.find((l) => l.lineId === lineId)
    if (!line) return

    set({
      tenderUi: {
        ...tenderUi,
        lines: tenderUi.lines.map((l) =>
          l.lineId === lineId
            ? {
                ...l,
                brandId: newBrandId,
                brandName: newBrandName,
              }
            : l
        ),
      },
    })
  },

  initNewTender: () => {
    set({ tenderUi: convertTenderToTenderUi(new Tender()) })
  },

  saveTender: async () => {
    const tenderUi = get().tenderUi
    if (!tenderUi) return false

    try {
      const tender = convertTenderUiToTender(tenderUi)
      if (tenderUi.id) {
        await tenderService.updateTender(tender)
      } else {
        await tenderService.createTender(tender)
      }
      enqueueLoadedSnackbar('Tender saved', 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to save tender')
      return false
    }
  },

  addItemGroup: (groupId: string) => {
    const tenderUi = get().tenderUi
    if (!tenderUi) return

    const items = useStockStore.getState().items
    const existingIds = new Set(tenderUi.lines.map((l) => l.stockItemId))

    const newLines = Object.values(items)
      .filter(
        (item) => item.groupId === groupId && !existingIds.has(item.id ?? '')
      )
      .map(createTenderLine)

    set({
      tenderUi: {
        ...tenderUi,
        lines: [...tenderUi.lines, ...newLines],
      },
    })
  },
})

export default createTendersSlice
